import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield,
  Target,
  Users,
  Award,
  Clock,
  Globe,
  Plane,
  ChevronRight,
  MapPin,
} from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Integrity",
      description: "Operating with honor and transparency in every mission and partnership.",
    },
    {
      icon: Target,
      title: "Precision",
      description: "Executing with military-grade accuracy and attention to detail.",
    },
    {
      icon: Clock,
      title: "Readiness",
      description: "72-hour mobilization capability for rapid deployment needs.",
    },
    {
      icon: Users,
      title: "Service",
      description: "Veteran-led teams dedicated to mission success and client satisfaction.",
    },
  ];

  const timeline = [
    {
      year: "Founded",
      title: "Veteran Vision",
      description: "AMR Global Holdings LLC established by military veterans with a mission to bring discipline and excellence to aviation logistics.",
    },
    {
      year: "Headquarters",
      title: "New Orleans Lakefront Airport",
      description: "Strategic positioning at KNEW airport for rapid aviation deployment and logistics operations.",
    },
    {
      year: "Compliance",
      title: "Government Ready",
      description: "Achieved full government contractor compliance including SAM.gov registration, CAGE code, and HUBZone alignment.",
    },
    {
      year: "Growth",
      title: "National Expansion",
      description: "Developing a national aviation and logistics readiness infrastructure network powered by veteran teams.",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1a365d]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">ABOUT US</p>
            <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">
              BUILT ON MILITARY DISCIPLINE
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed">
              AMR Global Holdings LLC is a veteran-founded company built on military discipline 
              and mission execution. We deliver rapid, reliable, and scalable aviation and 
              logistics support to government and commercial partners.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 right-0 w-1/3 h-full hidden lg:block opacity-20">
          <div className="w-full h-full bg-gradient-to-l from-[#c5a04a] to-transparent"></div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 bg-white" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">LEADERSHIP</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                ALEXANDER M. REID
              </h2>
              <p className="text-[#c5a04a] font-heading tracking-wider mb-4">CHIEF EXECUTIVE OFFICER</p>
              <p className="text-slate-600 leading-relaxed mb-6">
                As CEO of AMR Global Holdings LLC, Alexander M. Reid brings military leadership 
                experience and a commitment to excellence in aviation and logistics operations. 
                Under his leadership, AMR Global has positioned itself as a trusted government 
                contractor capable of supporting federal, state, and commercial clients.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                With a focus on veteran employment and mission-ready operations, the company 
                maintains 100% veteran workforce and 72-hour mobilization capability for 
                rapid deployment anywhere CONUS/OCONUS.
              </p>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <MapPin className="w-4 h-4 text-[#c5a04a]" />
                <span>New Orleans Lakefront Airport (KNEW)</span>
              </div>
            </div>
            <div className="relative">
              <div className="bg-slate-100 rounded-sm p-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_rapid-deploy-52/artifacts/omrsi8tb_1000028432.jpg"
                  alt="AMR Global Holdings Logo"
                  className="w-full max-w-md mx-auto"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#1a365d] p-6 rounded-sm text-white">
                <p className="font-heading text-lg">VETERAN POWERED.</p>
                <p className="font-heading text-[#c5a04a]">MISSION READY.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="card-tactical">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-[#c5a04a]" />
                </div>
                <h3 className="font-heading text-2xl text-[#1a365d] mb-4">OUR MISSION</h3>
                <p className="text-slate-600 leading-relaxed">
                  Deliver rapid, reliable, and scalable aviation and logistics support to 
                  government and commercial partners. We execute with military precision 
                  and veteran-powered teams to ensure mission success.
                </p>
              </CardContent>
            </Card>
            <Card className="card-tactical">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mb-6">
                  <Globe className="w-7 h-7 text-[#c5a04a]" />
                </div>
                <h3 className="font-heading text-2xl text-[#1a365d] mb-4">OUR VISION</h3>
                <p className="text-slate-600 leading-relaxed">
                  To build a national aviation and logistics readiness infrastructure network 
                  powered by veteran teams, serving as a trusted partner for government 
                  agencies and prime contractors across the United States.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">PRINCIPLES</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">CORE VALUES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="card-tactical text-center" data-testid={`value-card-${index}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-slate-100 rounded-sm flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-7 h-7 text-[#1a365d]" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1a365d] mb-3">{value.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-[#1a365d]" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">JOURNEY</p>
            <h2 className="font-heading text-3xl md:text-5xl text-white">OUR STORY</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {timeline.map((item, index) => (
              <div
                key={index}
                className="p-6 border border-slate-700 rounded-sm hover:border-[#c5a04a] transition-colors"
                data-testid={`timeline-item-${index}`}
              >
                <p className="text-[#c5a04a] font-heading text-sm tracking-wider mb-2">{item.year}</p>
                <h3 className="font-heading text-xl text-white mb-3">{item.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Headquarters */}
      <section className="py-20 bg-white" data-testid="headquarters-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1767491629923-009ffdc395d8?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Aircraft Operations"
                className="rounded-sm shadow-lg"
              />
            </div>
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">LOCATION</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                HEADQUARTERS
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-[#c5a04a] mt-1" />
                  <div>
                    <p className="font-medium text-[#1a365d]">AMR Global Holdings LLC</p>
                    <p className="text-slate-600">6001 Stars and Stripes Blvd, Suite 244</p>
                    <p className="text-slate-600">New Orleans, LA 70126</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Plane className="w-5 h-5 text-[#c5a04a] mt-1" />
                  <div>
                    <p className="font-medium text-[#1a365d]">New Orleans Lakefront Airport</p>
                    <p className="text-slate-600">Airport Code: KNEW</p>
                    <p className="text-slate-600">Strategic aviation deployment hub</p>
                  </div>
                </div>
              </div>
              <Link to="/contact">
                <Button className="btn-tactical btn-primary text-xs" data-testid="contact-us-btn">
                  Contact Us
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
